import { msg } from '@lingui/core/macro';
import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { Plus } from 'lucide-react';
import React, { ClipboardEvent, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import { UpsertStageTeamGroupParams } from 'modules/championship/domain/repositories/team-group.repository';
import { parseSortOrderInput, sanitizeSortOrderInput } from 'modules/championship/domain/utils/sanitize-sort-order-input';
import { upsertStageTeamGroup } from 'modules/championship/store/stages/stages.actions';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

const CreateStageGroupFormWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

export type CreateStageGroupFormHandle = {
  focus: () => void;
};

interface CreateStageGroupFormProps {
  stage: StageV2;
}

type CreateStageGroupFormValues = {
  name: string;
  sortOrder: string;
};

const handleSortOrderPaste = (e: ClipboardEvent<HTMLInputElement>) => {
  e.preventDefault();
};

const CreateStageGroupForm = React.forwardRef<CreateStageGroupFormHandle, CreateStageGroupFormProps>(({ stage }, ref) => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      setIsHighlighted(true);
    },
  }));

  const initialValues: CreateStageGroupFormValues = {
    name: '',
    sortOrder: '',
  };

  // `async` — чтобы промис был у каждой ветки: Formik сбрасывает `isSubmitting`, только получив промис, и иначе
  // кнопка «Создать группу» застревала со спиннером (после пустого названия и раньше — после ошибки).
  const handleSubmit = useCallback(
    async (values: CreateStageGroupFormValues, { resetForm }: FormikHelpers<CreateStageGroupFormValues>) => {
      if (!values.name.length) {
        return;
      }

      const params: UpsertStageTeamGroupParams = {
        stageId: stage.id,
        name: values.name,
        teamIds: [],
        sortOrder: parseSortOrderInput(values.sortOrder),
      };

      // Форма сбрасывается только на успехе: раньше и на провале, и введённое название пропадало.
      try {
        await dispatch(upsertStageTeamGroup(params)).unwrap();
        resetForm();
      } catch (error) {
        toastApiError(error, msg`Не удалось создать группу`);
      }
    },
    [dispatch, stage.id, toastApiError],
  );

  return (
    <CreateStageGroupFormWrapper className="w-full">
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit} className={'flex justify-between gap-5'}>
            <div>
              <UDButton variant={'primary'}>
                <Trans>Таблица</Trans>
              </UDButton>
            </div>
            <div className={'flex gap-5'}>
              <div
                className={`w-100 rounded-lg ${isHighlighted ? 'animate-highlight' : ''}`}
                onAnimationEnd={() => setIsHighlighted(false)}
              >
                <UDFormInput ref={inputRef} name="name" placeholder={t`Название группы`} showValidateIndicator={false} />
              </div>
              <div className="w-40">
                <UDFormInput
                  name="sortOrder"
                  inputMode="numeric"
                  placeholder={t`Порядковый номер (необязательно)`}
                  showValidateIndicator={false}
                  onPaste={handleSortOrderPaste}
                  onChange={(e) => {
                    formProps.setFieldValue('sortOrder', sanitizeSortOrderInput(e.target.value));
                  }}
                />
              </div>
              <UDButton disabled={!formProps.values.name.length} type="submit" variant="primary" className={'whitespace-nowrap'}>
                <Plus size={20} />
                <Trans>Создать группу</Trans>
              </UDButton>
            </div>
          </form>
        )}
      </Formik>
    </CreateStageGroupFormWrapper>
  );
});

export default CreateStageGroupForm;
